import axios from "axios";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { setLoader } from "../../Store/Reducers/UserReducer";
import { useContextMenu } from "../../Layout Component/ContextMenu";
import Folliculogram, { parseFolliDate, readFolliculogramVersions } from "./Folliculogram";
import DicomViewer from "./DicomViewer";
import { coupleName, toRow } from "./TreatmentWithUs";
import { appointmentsIn, cycleSpan, follicleTotals, keyDatesOf, latestDays, printFolliculogram, printSchedule, printWallChart } from "./cyclePrint";

// A treatment cycle, opened from the overview or the treatment list, to LOOK
// at: the folliculogram and the digital images, nothing to edit. Laid out like
// the clinical Folliculogram & Imaging page: the treatment and its dates, the
// follicle counts, the key dates and visits as chips, then the grid. Print
// gives the folliculogram, or a medication schedule (drugs and appointments).
// (The older Details page, TreatmentOutcome, is still there from the context
// menu; it is where the patient records a pregnancy outcome.)

// A page that cannot draw says so, instead of blanking the whole portal.
class Guard extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <p className="ox-hint" role="alert">
          This could not be drawn ({String(this.state.error.message || this.state.error)}). Please go back and try again.
        </p>
      );
    }
    return this.props.children;
  }
}

// What to say when a folliculogram is on file but nothing could be read from it.
function Unreadable({ json }) {
  const text = typeof json === "string" ? json : JSON.stringify(json);
  return (
    <div className="ox-hint" role="status">
      <p>A folliculogram is saved for this treatment, but its layout was not recognised, so it cannot be drawn here yet.</p>
      <details>
        <summary>Show what was received</summary>
        <pre style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflow: "auto" }}>{String(text).slice(0, 1500)}</pre>
      </details>
    </div>
  );
}

const dayText = (raw) => {
  if (!raw || String(raw).startsWith("0001-01-01")) return null;
  const m = moment(raw);
  return m.isValid() && m.year() > 1 ? m.format("DD-MMM-YYYY") : null;
};

const KEY_ICONS = [
  [/trigger|injection/i, "fa-syringe"],
  [/egg|oocyte/i, "fa-egg"],
  [/transfer/i, "fa-baby"],
  [/pregnan/i, "fa-vial"],
  [/fertili/i, "fa-microscope"],
];
const VISIT_ICONS = [
  [/scan|follic|ultrasound/i, "fa-wave-square"],
  [/blood/i, "fa-droplet"],
  [/consult|review|clinic/i, "fa-user-doctor"],
];
const iconFor = (list, text, fallback) => (list.find(([re]) => re.test(text)) || [null, fallback])[1];

// A dose written on a day of the folliculogram: { date, drug, dose }.
function dosesOf(days) {
  const out = [];
  days.forEach((d) => {
    const m = parseFolliDate(d.date);
    (d.DrugsValue || []).forEach((x) => {
      const dose = x?.DrugsVAlues ?? x?.DrugsValues;
      if (x?.DrugName && dose !== undefined && String(dose).trim() !== "") out.push({ date: m, drug: x.DrugName, dose: String(dose) });
    });
  });
  return out;
}

export default function TreatmentCycleView() {
  const url = process.env.REACT_APP_OXAR_API_URL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { show, menu } = useContextMenu();

  // While this page is open, the browser's own Print also defaults to landscape.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-ox-landscape", "true");
    style.textContent = "@media print { @page { size: A4 landscape; margin: 10mm; } .sidebar, .navbar1, .testD, footer, .ox-folhead__go, .ox-tabs, .ox-crumbs { display: none !important; } .page-content { margin: 0 !important; } .folli-grid-scroll { max-height: none !important; overflow: visible !important; } }";
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const id = state?.id || localStorage.getItem("Tretmentid");
  const [tab, setTab] = useState(state?.tab === "images" ? "images" : "folliculogram");
  const [cycle, setCycle] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    let alive = true;
    setCycle(null);
    setError("");
    (async () => {
      try {
        dispatch(setLoader(true));
        const res = await axios(`${url}api/Treatment/GetTreatmentCycleById?id=${id}`);
        if (!alive) return;
        // The images are found by the cycle's name.
        const name = state?.name || res.data?.content?.name || "";
        sessionStorage.setItem("Treatmentid", id);
        sessionStorage.setItem("TreatmentName", name);
        localStorage.setItem("Tretmentid", id);
        // Where the scans may be filed. SharePoint folders are named
        // "<type> <date> - <patient> & <partner>" ("/" written "-"), and the date
        // may be the treatment's start or the first day of its folliculogram, so
        // each is offered; any folder / SharePoint field on the record goes first.
        const content = res.data?.content || {};
        const alt = [];
        Object.keys(content).forEach((k) => {
          const v = content[k];
          if (/sharepoint|folder|documentlocation/i.test(k) && typeof v === "string" && v.trim() && !/^https?:/i.test(v.trim())) alt.push(v.trim());
          else if (/sharepoint|folder/i.test(k) && typeof v === "string" && /^https?:/i.test(v.trim())) alt.push(decodeURIComponent(v.trim().replace(/\/+$/, "").split("/").pop()));
        });
        const typeName = state?.type || content.bcrm_treatment_type_os_name;
        const couple = state?.couple || "";
        const [patientName, partnerName] = couple.split(" & ");
        const startDay = state?.start || dayText(content.bcrm_treatment_start_date || content.treatmentStartDate);
        const folliDay = latestDays(content.folliculagramJSON).days.map((d) => parseFolliDate(d.date)).filter(Boolean).sort((x, y) => x - y)[0];
        const dates = [...new Set([folliDay && moment(folliDay).format("DD-MMM-YYYY"), startDay].filter(Boolean))];
        if (typeName && patientName) {
          const t = typeName.replace(/\s*\/\s*/g, "-");
          dates.forEach((d) => {
            if (partnerName) {
              alt.push(`${t} ${d} - ${patientName} - ${partnerName}`);
              alt.push(`${t} ${d} - ${patientName} & ${partnerName}`);
            } else {
              alt.push(`${t} ${d} - ${patientName}`);
            }
          });
        }
        sessionStorage.setItem("TreatmentAltNames", JSON.stringify(alt));
        // For the folder-list fallback: the dates this treatment can carry and
        // the words its folder should share.
        sessionStorage.setItem("TreatmentMatch", JSON.stringify({
          dates: [startDay, folliDay && moment(folliDay).format("DD-MMM-YYYY")].filter(Boolean),
          type: typeName || "",
          words: couple,
        }));
        // Only now may the page (and the image viewer under it) see the cycle.
        setCycle(res.data?.content || null);
      } catch {
        if (alive) setError("This treatment could not be loaded. Please go back and try again.");
      } finally {
        dispatch(setLoader(false));
      }
      try {
        const uid = localStorage.getItem("UserID");
        const list = await axios(`${url}api/Treatment/GetTreatmentCyclesByContactId?id=${uid}`);
        const couple = coupleName();
        if (alive) {
          setCycles(
            (list.data?.content?.treatmentcycleDTO || [])
              .map((c) => ({ c, row: toRow(c, undefined, couple) }))
              .sort((x, y) => (y.row.start ? y.row.start.valueOf() : 0) - (x.row.start ? x.row.start.valueOf() : 0)),
          );
        }
      } catch {
        // The dropdown simply lists the one treatment.
      }
      try {
        const uid = localStorage.getItem("UserID");
        const a = await axios(`${url}api/AppointmentDosage/GetAppointments?contactId=${uid}`);
        if (alive) setAppointments(a.data?.content || []);
      } catch {
        // Visits and the printed schedule simply have no appointments.
      }
    })();
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = state?.title || cycle?.name || "Treatment";
  const type = state?.type || cycle?.bcrm_treatment_type_os_name;
  const start = state?.start || dayText(cycle?.bcrm_treatment_start_date || cycle?.treatmentStartDate);
  const couple = state?.couple || "";

  const { days, number, count } = useMemo(() => latestDays(cycle?.folliculagramJSON), [cycle]);
  const span = useMemo(() => cycleSpan(days), [days]);
  const totals = useMemo(() => follicleTotals(days), [days]);
  const keyDates = useMemo(() => keyDatesOf(cycle, span?.from), [cycle, span]);
  const visits = useMemo(() => appointmentsIn(appointments, span).slice(0, 12), [appointments, span]);
  // Booked visits and key dates, as pins on the grid's day headings.
  const markers = useMemo(() => [
    ...keyDates.map((k) => ({ kind: "key", date: k.date.toDate(), label: k.label, icon: iconFor(KEY_ICONS, k.label, "fa-star"), title: `${k.label} ${k.date.format("ddd DD-MMM")}${k.day ? ` (D${k.day})` : ""}` })),
    ...visits.map((v) => {
      const label = v.appointmentlibraryitemName || v.subject || "Appointment";
      return { kind: "visit", date: moment(v.startTime).toDate(), label, icon: iconFor(VISIT_ICONS, `${label} ${v.subject || ""}`, "fa-calendar-check"), title: `${label} · ${moment(v.startTime).format("ddd DD-MMM HH:mm")}${v.roomName ? ` · ${v.roomName}` : ""}` };
    }),
  ], [keyDates, visits]);

  // The drug regime by date: a row for each day a dose is due, a column per drug.
  const regime = useMemo(() => {
    const doses = dosesOf(days);
    const drugs = [...new Set(doses.map((d) => d.drug))];
    const dates = [...new Set(doses.filter((d) => d.date).map((d) => moment(d.date).format("YYYY-MM-DD")))].sort();
    return { drugs, rows: dates.map((k) => ({ date: moment(k), cells: drugs.map((n) => doses.find((d) => d.date && moment(d.date).format("YYYY-MM-DD") === k && d.drug === n)?.dose || "") })) };
  }, [days]);
  const unreadable = cycle?.folliculagramJSON && readFolliculogramVersions(cycle.folliculagramJSON).length === 0;

  const printWith = (fn) => () => {
    if (!cycle) return;
    const ok = fn({ title: `${title}${couple ? ` · ${couple}` : ""}`, couple, type, start, cycle, appointments });
    if (!ok) toast.warn("Your browser blocked the print window. Please allow pop-ups for this site.");
  };
  const printFolli = () => {
    if (!cycle) return;
    const ok = printFolliculogram({ title: `${title}${couple ? ` \u00b7 ${couple}` : ""}`, couple, type, start, cycle, appointments, keyDates });
    if (!ok) toast.warn("Your browser blocked the print window. Please allow pop-ups for this site.");
  };
  const printSched = printWith(printSchedule);
  const printWall = () => {
    if (!cycle) return;
    const ok = printWallChart({ title: `${title}${couple ? ` \u00b7 ${couple}` : ""}`, couple, type, start, cycle, appointments, keyDates });
    if (!ok) toast.warn("Your browser blocked the print window. Please allow pop-ups for this site.");
  };

  return (
    <div
      className="ox-cycle"
      onContextMenu={(e) => show(e, [
        { label: "Folliculogram", onClick: () => setTab("folliculogram") },
        { label: "Images", onClick: () => setTab("images") },
        { divider: true },
        { label: "Print (landscape)", onClick: printFolli },
        { label: "Print medication schedule", onClick: printSched },
        { label: "Print wall chart", onClick: printWall },
        { divider: true },
        { label: "Details and outcome", onClick: () => navigate("/TreatmentOutcome", { state: { Tid: id } }) },
        { label: "Back to my treatments", onClick: () => navigate("/Treatments") },
      ])}
    >
      <div className="ox-folhead">
        <span className="ox-pillbox"><em>Treatment</em><strong>{title}{couple ? ` · ${couple}` : ""}</strong></span>
        {span && (
          <span className="ox-pillbox"><em>Dates</em><strong>{span.from.format("DD-MMM-YYYY")} {"–"} {span.to.format("DD-MMM-YYYY")}</strong><small>{span.count} days</small></span>
        )}
        {!span && start && <span className="ox-pillbox"><em>Start</em><strong>{start}</strong></span>}
        {number !== null && <span className="ox-pillbox"><em>Version</em><strong>{number}{count > 1 ? ` of ${count}` : ""}</strong><small>Current</small></span>}
        {span && (
          <>
            <span className="ox-count ox-count--r">Right {totals.right}</span>
            <span className="ox-count ox-count--l">Left {totals.left}</span>
          </>
        )}
        <span className="ox-folhead__go">
          <span className="ox-chip ox-chip--viewing">Read only</span>
          <button type="button" className="ox-btn" onClick={printFolli} disabled={!cycle}>Print</button>
          <button type="button" className="ox-btn" onClick={printSched} disabled={!cycle}>Print medication schedule</button>
          <button type="button" className="ox-btn ox-btn--primary" onClick={printWall} disabled={!cycle} title="A one-page calendar to put on the wall and tick off">Print wall chart</button>
          <button type="button" className="ox-btn" onClick={() => navigate(-1)}>Back</button>
        </span>
      </div>

      {error && <p className="ox-hint" role="alert">{error}</p>}

      <div className="ox-tabs" role="tablist">
        {[["folliculogram", "Folliculogram", "fa-wave-square"], ["images", "Images", "fa-images"]].map(([key, label, icon]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key}
            className={`ox-tab${tab === key ? " is-active" : ""}`} onClick={() => setTab(key)}>
            <i className={`fa-solid ${icon}`} aria-hidden="true" /> {label}
          </button>
        ))}
      </div>

      {tab === "folliculogram" && (keyDates.length > 0 || visits.length > 0) && (
        <div className="ox-chips">
          {keyDates.length > 0 && (
            <div className="ox-chips__row">
              <b>Key dates</b>
              {keyDates.map((k) => (
                <span key={k.label} className="ox-keychip">
                  <strong>{k.label}</strong> {k.date.format("DD-MMM")}
                  {k.day !== null && <em>D{k.day}</em>}
                </span>
              ))}
            </div>
          )}
          {visits.length > 0 && (
            <div className="ox-chips__row">
              <b>Visits</b>
              {visits.map((v) => (
                <span key={`${v.id}-${v.startTime}`} className="ox-keychip ox-keychip--visit" title={`${v.subject || ""} · ${moment(v.startTime).format("ddd DD-MMM HH:mm")}`}>
                  <strong>{v.appointmentlibraryitemName || v.subject || "Appointment"}</strong> {moment(v.startTime).format("DD-MMM")}
                  {span && <em>D{moment(v.startTime).startOf("day").diff(span.from.clone().startOf("day"), "days") + 1}</em>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ox-section">
        <div className="ox-section__body">
          {tab === "folliculogram" ? (
            cycle ? (
              <Guard>
                {unreadable ? <Unreadable json={cycle.folliculagramJSON} /> : (
                  <Folliculogram json={cycle.folliculagramJSON} markers={markers}>
                    <section className="ox-below">
                      <h5>Appointments in this treatment</h5>
                      {visits.length === 0 ? <p className="ox-hint">No appointments are booked in this treatment.</p> : (
                        <table className="ox-mini">
                          <thead><tr><th>When</th><th>What</th><th>Where</th><th>Status</th></tr></thead>
                          <tbody>
                            {visits.map((v) => (
                              <tr key={`${v.id}-${v.startTime}`}>
                                <td>{moment(v.startTime).format("ddd DD-MMM-YYYY HH:mm")}{span && <em> D{moment(v.startTime).startOf("day").diff(span.from.clone().startOf("day"), "days") + 1}</em>}</td>
                                <td>{v.subject || v.appointmentlibraryitemName || "Appointment"}</td>
                                <td>{v.roomName || "—"}</td>
                                <td>{v.bookingStatusName || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </section>
                    <section className="ox-below">
                      <h5>Drug regime by date</h5>
                      {regime.rows.length === 0 ? <p className="ox-hint">No drugs have been scheduled in this treatment yet.</p> : (
                        <div className="ox-below__scroll">
                          <table className="ox-mini">
                            <thead><tr><th>Date</th>{regime.drugs.map((n) => <th key={n}>{n}</th>)}</tr></thead>
                            <tbody>
                              {regime.rows.map((r) => (
                                <tr key={r.date.format("YYYY-MM-DD")}>
                                  <td>{r.date.format("ddd DD-MMM-YYYY")}{span && <em> D{r.date.clone().startOf("day").diff(span.from.clone().startOf("day"), "days") + 1}</em>}</td>
                                  {r.cells.map((c, i) => <td key={i} className={c ? "is-dose" : ""}>{c}</td>)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  </Folliculogram>
                )}
              </Guard>
            ) : !error && <p className="ox-hint">Loading...</p>
          ) : (
            // Mounted once the cycle is known: the viewer finds the images by
            // the cycle name stored above.
            cycle ? <Guard><DicomViewer key={id} /></Guard> : !error && <p className="ox-hint">Loading...</p>
          )}
        </div>
      </div>
      {menu}
    </div>
  );
}
